import type { ComponentPropsWithoutRef, JSX } from 'react';
import type { MDXComponents } from 'mdx/types';
import {
  BeforeAfter,
  Callout,
  CodeBlock,
  Compare,
  Figure,
  SmartLink,
  Step,
  Steps,
  Video,
} from './article/index.js';

// The single source of truth for how MDX elements render. Passed to every
// article's MDX component, so a tweak here restyles every article at once.
// Typography matches the getting-started / overview pages (editorial scale,
// hairline rules, #0c0c0c code surface, lime/cyan accents).
//
// Spacing note: the app remaps Tailwind's numeric scale to its own non-linear
// tokens (--space-5 = 32px, -6 = 48px, -7 = 64px, -8 = 96px), so spacing here
// stays at -5/-6 and below to avoid the oversized -7/-8 gaps.

function Anchor({ href, children }: ComponentPropsWithoutRef<'a'>): JSX.Element {
  return <SmartLink href={href}>{children}</SmartLink>;
}

function Img({ src, alt }: ComponentPropsWithoutRef<'img'>): JSX.Element {
  return <Figure src={typeof src === 'string' ? src : ''} alt={alt ?? ''} />;
}

function Code({ className, children }: ComponentPropsWithoutRef<'code'>): JSX.Element {
  // Fenced code carries a `language-*` class (it sits inside <pre> → CodeBlock,
  // which owns the surface). Inline code has no class → render the cyan pill.
  if (typeof className === 'string' && className.startsWith('language-')) {
    return <code className={className}>{children}</code>;
  }
  return (
    <code className="rounded-[3px] border border-white/[0.06] bg-[#0c0c0c] px-1.5 py-0.5 font-mono text-[13.5px] text-accent-cyan">
      {children}
    </code>
  );
}

export const mdxComponents: MDXComponents = {
  h1: ({ id, children }: ComponentPropsWithoutRef<'h1'>) => (
    <h1
      id={id}
      className="mb-4 mt-10 scroll-mt-24 font-sans text-[clamp(28px,4vw,40px)] font-light leading-[1.1] tracking-[-0.02em] text-primary"
    >
      {children}
    </h1>
  ),
  h2: ({ id, children }: ComponentPropsWithoutRef<'h2'>) => (
    <h2
      id={id}
      className="mb-3 mt-12 scroll-mt-24 font-sans text-[27px] font-light leading-[1.2] tracking-[-0.02em] text-primary"
    >
      {children}
    </h2>
  ),
  h3: ({ id, children }: ComponentPropsWithoutRef<'h3'>) => (
    <h3
      id={id}
      className="mb-2 mt-5 scroll-mt-24 font-sans text-[19px] font-medium leading-[1.3] tracking-[-0.01em] text-primary"
    >
      {children}
    </h3>
  ),
  h4: ({ id, children }: ComponentPropsWithoutRef<'h4'>) => (
    <h4 id={id} className="mb-1 mt-5 scroll-mt-24 font-sans text-[16px] font-semibold text-primary">
      {children}
    </h4>
  ),
  p: ({ children }: ComponentPropsWithoutRef<'p'>) => (
    <p className="my-4 text-[17px] leading-[1.8] text-secondary">{children}</p>
  ),
  a: Anchor,
  ul: ({ children }: ComponentPropsWithoutRef<'ul'>) => (
    <ul className="my-4 list-disc space-y-2 pl-5 text-[17px] leading-[1.7] text-secondary marker:text-muted">
      {children}
    </ul>
  ),
  ol: ({ children }: ComponentPropsWithoutRef<'ol'>) => (
    <ol className="my-4 list-decimal space-y-2 pl-5 text-[17px] leading-[1.7] text-secondary marker:text-muted">
      {children}
    </ol>
  ),
  li: ({ children }: ComponentPropsWithoutRef<'li'>) => <li className="pl-1">{children}</li>,
  strong: ({ children }: ComponentPropsWithoutRef<'strong'>) => (
    <strong className="font-medium text-primary">{children}</strong>
  ),
  em: ({ children }: ComponentPropsWithoutRef<'em'>) => <em className="italic">{children}</em>,
  hr: () => <hr className="my-10 border-white/[0.08]" />,
  blockquote: ({ children }: ComponentPropsWithoutRef<'blockquote'>) => (
    <blockquote className="my-5 border-l-2 border-white/15 pl-4 text-[17px] italic leading-[1.7] text-secondary">
      {children}
    </blockquote>
  ),
  img: Img,
  pre: ({ children }: ComponentPropsWithoutRef<'pre'>) => <CodeBlock>{children}</CodeBlock>,
  code: Code,
  table: ({ children }: ComponentPropsWithoutRef<'table'>) => (
    <div className="my-5 overflow-x-auto rounded-md border border-white/[0.06]">
      <table className="w-full border-collapse text-[15px]">{children}</table>
    </div>
  ),
  thead: ({ children }: ComponentPropsWithoutRef<'thead'>) => (
    <thead className="bg-white/[0.02]">{children}</thead>
  ),
  tbody: ({ children }: ComponentPropsWithoutRef<'tbody'>) => <tbody>{children}</tbody>,
  tr: ({ children }: ComponentPropsWithoutRef<'tr'>) => (
    <tr className="border-b border-white/[0.06] last:border-0">{children}</tr>
  ),
  th: ({ children }: ComponentPropsWithoutRef<'th'>) => (
    <th className="px-3 py-2 text-left font-mono text-[11px] uppercase tracking-[0.08em] text-muted">
      {children}
    </th>
  ),
  td: ({ children }: ComponentPropsWithoutRef<'td'>) => (
    <td className="px-3 py-2 align-top text-secondary">{children}</td>
  ),
  // Interactive kit — authored as JSX tags in MDX.
  Figure,
  BeforeAfter,
  Compare,
  Callout,
  Steps,
  Step,
  Video,
};
